/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Box, Button, DialogTitle, Grid, Typography } from '@mui/material';
import React from 'react';
import { ChevronLeft, Close } from '@opengeoweb/theme';
import type { ButtonVariant } from '@opengeoweb/theme';
import { useSharedTranslation } from '../../utils/i18n';
import { CustomIconButton } from '../CustomIconButton';
import { ToggleMenu } from '../ToggleMenu';
import type { DialogAction } from './types';
import type { MenuItemType } from '../ToggleMenu';

export type HeaderAction = MenuItemType & {
  variant?: ButtonVariant;
};

const Title: React.FC<{ title: string }> = ({ title }) => (
  <Typography
    component="span"
    data-testid="customDialog-title"
    sx={{
      fontSize: '16px',
      fontWeight: 500,
      height: 24,
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      color: 'geowebColors.typographyAndIcons.text',
    }}
  >
    {title}
  </Typography>
);

export const CustomDialogHeader: React.FC<{
  headerActions?: HeaderAction[];
  title?: string;
  onClose: (reason: DialogAction) => void;
}> = ({ headerActions, title, onClose }) => {
  const { t } = useSharedTranslation();
  return !headerActions ? (
    <DialogTitle
      id="dialog-title"
      sx={{
        zIndex: 1,
        padding: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      {title ? <Title title={title} /> : null}
      <CustomIconButton
        aria-label="close"
        data-testid="customDialog-close"
        onClick={(): void => onClose('CLOSED')}
        sx={{
          '&.MuiIconButton-root': {
            color: 'geowebColors.greys.accessible',
          },
        }}
      >
        <Close data-testid="CloseIcon" />
      </CustomIconButton>
    </DialogTitle>
  ) : (
    <Grid
      alignItems="center"
      aria-label="Dialog options header"
      container
      data-testid="dialog-action-header"
      sx={{
        boxShadow: 1,
        py: 0.5,
        px: 1,
        zIndex: 1,
      }}
    >
      <Grid size={4}>
        <Button
          aria-label="close"
          data-testid="contentdialog-close"
          onClick={() => onClose('CLOSED')}
          startIcon={<ChevronLeft />}
          sx={{
            textTransform: 'uppercase',
          }}
          variant="flat"
        >
          {t('shared-customdialog-back')}
        </Button>
      </Grid>
      <Grid
        size={4}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          textAlign: 'center',
        }}
      >
        {title ? <Title title={title} /> : null}
      </Grid>
      <Grid size={4}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            'button:not(:first-of-type)': { marginLeft: 1 },
          }}
        >
          <Box>
            <Box
              display={{
                xs: 'none',
                lg: 'flex',
              }}
            >
              {headerActions.map(
                ({ action, text, variant, isDisabled = false }) => (
                  <Button
                    disabled={isDisabled}
                    key={`${text}-${variant}`}
                    onClick={action}
                    sx={{ minWidth: 96 }}
                    variant={variant ?? 'flat'}
                  >
                    {text}
                  </Button>
                ),
              )}
            </Box>
            <Box display={{ sm: 'block', lg: 'none' }}>
              {headerActions.length > 0 && (
                <ToggleMenu
                  menuItems={headerActions.map(
                    ({ text, action, isDisabled, isSelected }) => ({
                      text,
                      action,
                      isDisabled,
                      isSelected,
                    }),
                  )}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default CustomDialogHeader;
